import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Box, Group, Menu } from '@mantine/core';
import { countVisibleLinks, splitOverflowLinks, splitAroundLogo } from './siteHeaderLayout';
import classes from './SiteHeader.module.css';

/** Cible d'un lien du menu : une page du site (son ancre), ou un lien choisi
 *  dont la cible est déjà résolue (page, bloc, URL externe…). */
export function menuLinkAnchor(link) {
  return link.anchor || { href: `#${link.slug}` };
}

/** L'écart entre deux liens, en px — mesuré et appliqué avec la même valeur. */
export const NAV_GAP = 20;

/**
 * La rangée de liens du menu : une seule ligne, le reste dans « Plus ».
 *
 * La mesure ne peut pas se faire sur les liens affichés (ils sont déjà coupés) :
 * une copie invisible de la liste entière donne les largeurs, puis
 * {@link countVisibleLinks} dit combien tiennent. Le calcul lui-même est pur et
 * testé à côté (`siteHeaderLayout.js`) ; ici il n'y a que la mesure et le rendu.
 *
 * `logo` n'est renseigné que pour le style « liens répartis », où le logo vit
 * entre les deux moitiés : sa largeur est alors retirée de la place disponible.
 *
 * `renderMoreLabel` vient de `SiteHeader` : « Plus » est un lien du menu comme
 * les autres — même typographie, même couleur, et même mise en avant quand la
 * page affichée est tombée dans le débordement. Il est donc fabriqué là où le
 * style des liens est connu.
 */
const SiteHeaderNav = ({
  links = [],
  renderLink,
  renderMoreLabel,
  isActive = () => false,
  accent = '',
  justify = 'flex-end',
  logo = null,
  visibleFrom,
}) => {
  const rowRef = useRef(null);
  const probeRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(links.length);

  const measure = useCallback(() => {
    const row = rowRef.current;
    const probe = probeRef.current;
    if (!row || !probe) return;

    const widths = [...probe.querySelectorAll('[data-nav-probe-item]')]
      .map((node) => node.offsetWidth);
    const moreWidth = probe.querySelector('[data-nav-probe-more]')?.offsetWidth || 0;
    const logoWidth = probe.querySelector('[data-nav-probe-logo]')?.offsetWidth || 0;
    const available = row.clientWidth - (logoWidth ? logoWidth + (NAV_GAP * 2) : 0);

    setVisibleCount(countVisibleLinks(widths, { gap: NAV_GAP, moreWidth, available }));
  }, []);

  useEffect(() => {
    measure();
    const row = rowRef.current;
    const onResize = () => window.requestAnimationFrame(measure);
    window.addEventListener('resize', onResize);
    /* La largeur de la rangée ne dépend pas de ce qu'elle contient (elle prend
       la place restante, sans retour à la ligne) : l'observer ne se rappelle
       donc pas lui-même en boucle. */
    const observer = row && typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(onResize)
      : null;
    if (observer) observer.observe(row);
    return () => {
      window.removeEventListener('resize', onResize);
      if (observer) observer.disconnect();
    };
  }, [measure, links]);

  const { visible, overflow } = splitOverflowLinks(links, visibleCount);

  /* La page affichée peut avoir débordé : « Plus » porte alors la mise en avant
     à sa place, sinon rien ne dit plus où on est. */
  const moreLabel = renderMoreLabel(overflow.some((link) => isActive(link.slug)));

  const moreMenu = overflow.length > 0 ? (
    <Menu shadow="md" width={300} withinPortal={false} position="bottom-end">
      <Menu.Target>{moreLabel}</Menu.Target>
      {/* La liste peut couvrir la moitié d'un site : elle défile plutôt que de
          dépasser de l'écran. */}
      <Menu.Dropdown style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {overflow.map((link, index) => (
          <React.Fragment key={link.slug}>
            {index > 0 && <Menu.Divider />}
            {/* La page en gras, ses titres en dessous : sans cette différence,
                dix-huit entrées de même graisse se lisaient comme une seule
                liste sans hiérarchie. */}
            <Menu.Item
              component="a"
              {...menuLinkAnchor(link)}
              fw={isActive(link.slug) ? 700 : 600}
              c={isActive(link.slug) ? (accent || undefined) : undefined}
            >
              {link.title}
            </Menu.Item>
            {/* Les sous-titres H2 aplatis et rattachés par un filet, comme dans
                le tiroir mobile : un sous-menu dans un sous-menu ne s'attrape
                pas à la souris. */}
            {(link.children || []).map((child) => (
              <Menu.Item
                key={`${link.slug}-${child.slug}`}
                component="a"
                href={`#${child.slug}`}
                fz="sm"
                c={isActive(child.slug) ? (accent || undefined) : 'gray.7'}
                fw={isActive(child.slug) ? 700 : undefined}
                ml={14}
                style={{ borderLeft: '2px solid var(--mantine-color-gray-3)', borderRadius: 0 }}
              >
                {child.title}
              </Menu.Item>
            ))}
          </React.Fragment>
        ))}
      </Menu.Dropdown>
    </Menu>
  ) : null;

  const halves = logo ? splitAroundLogo(visible) : null;

  return (
    <Box ref={rowRef} className={classes.links} visibleFrom={visibleFrom}>
      {/* Copie de mesure : la liste entière, hors flux et invisible. */}
      <Box ref={probeRef} className={classes.navProbe} aria-hidden>
        {links.map((link) => (
          <span data-nav-probe-item="" key={`probe-${link.slug}`}>{renderLink(link)}</span>
        ))}
        <span data-nav-probe-more="">{moreLabel}</span>
        {logo ? <span data-nav-probe-logo="">{logo}</span> : null}
      </Box>

      {halves ? (
        <Group justify="space-between" align="center" wrap="nowrap" gap={NAV_GAP}>
          <Group gap={NAV_GAP} wrap="nowrap">{halves.left.map(renderLink)}</Group>
          {logo}
          <Group gap={NAV_GAP} wrap="nowrap">
            {halves.right.map(renderLink)}
            {moreMenu}
          </Group>
        </Group>
      ) : (
        <Group gap={NAV_GAP} wrap="nowrap" justify={justify}>
          {visible.map(renderLink)}
          {moreMenu}
        </Group>
      )}
    </Box>
  );
};

export default SiteHeaderNav;
